import { useState, useRef, useCallback } from "react";
import {
  RECORDING_BAR_COUNT,
  PLAYBACK_BAR_COUNT,
  idleWaveBars,
  downsampleWaveform,
  extractPeaksFromBlob,
} from "../utils/audioWaveform";

const MIN_RECORDING_BYTES = 256;

function pickSupportedAudioMimeType() {
  if (typeof MediaRecorder === "undefined") return undefined;
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
    "audio/ogg",
  ];
  for (const mime of candidates) {
    if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return undefined;
}

export function useVoiceRecorder(onRecordingStop, callbacks) {
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [waveBars, setWaveBars] = useState(() => idleWaveBars());
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const chunks = useRef([]);
  const cancelledRef = useRef(false);
  const stoppingRef = useRef(false);
  const mimeTypeRef = useRef("audio/webm");
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;
  const waveHistoryRef = useRef([]);
  const recordingTimeRef = useRef(0);
  const analyserCleanupRef = useRef(null);

  const stopAnalyser = useCallback(() => {
    analyserCleanupRef.current?.();
    analyserCleanupRef.current = null;
  }, []);

  const startAnalyser = useCallback(
    (stream) => {
      stopAnalyser();
      waveHistoryRef.current = [];
      setWaveBars(idleWaveBars());

      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.35;
      source.connect(analyser);
      const data = new Uint8Array(analyser.fftSize);
      let raf = 0;
      let mounted = true;

      const tick = () => {
        if (!mounted) return;
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i += 1) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.max(0, Math.min(1, Math.sqrt(sum / data.length) * 3.2));
        waveHistoryRef.current.push(rms);
        if (waveHistoryRef.current.length > 2400) {
          waveHistoryRef.current = waveHistoryRef.current.slice(-1800);
        }
        const history = waveHistoryRef.current;
        const next =
          history.length >= RECORDING_BAR_COUNT
            ? history.slice(-RECORDING_BAR_COUNT)
            : [...idleWaveBars().slice(history.length), ...history];
        setWaveBars(next);
        raf = requestAnimationFrame(tick);
      };
      void ctx.resume?.();
      raf = requestAnimationFrame(tick);

      analyserCleanupRef.current = () => {
        mounted = false;
        cancelAnimationFrame(raf);
        try {
          source.disconnect();
        } catch {
          // ignore
        }
        void ctx.close();
      };
    },
    [stopAnalyser]
  );

  const startRecording = useCallback(async () => {
    if (recording || isFinalizing || stoppingRef.current) return;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    cancelledRef.current = false;

    const mimeType = pickSupportedAudioMimeType();
    mimeTypeRef.current = mimeType ?? "audio/webm";
    const mediaRecorder = mimeType
      ? new MediaRecorder(stream, { mimeType })
      : new MediaRecorder(stream);
    mediaRecorderRef.current = mediaRecorder;

    recordingTimeRef.current = 0;
    setRecordingTime(0);
    timerRef.current = setInterval(() => {
      recordingTimeRef.current += 1;
      setRecordingTime(recordingTimeRef.current);
    }, 1000);

    startAnalyser(stream);

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.current.push(e.data);
    };

    mediaRecorder.onstop = async () => {
      const recordedChunks = [...chunks.current];
      chunks.current = [];
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      stopAnalyser();
      const micWaveform = downsampleWaveform(waveHistoryRef.current, PLAYBACK_BAR_COUNT);
      const micDurationMs = Math.max(
        recordingTimeRef.current * 1000,
        Math.round((waveHistoryRef.current.length / 60) * 1000)
      );
      waveHistoryRef.current = [];
      setWaveBars(idleWaveBars());

      const wasCancelled = cancelledRef.current;
      cancelledRef.current = false;

      try {
        if (wasCancelled) {
          callbacksRef.current?.onRecordingCancelled?.();
          return;
        }

        const blob = new Blob(recordedChunks, { type: mimeTypeRef.current });
        if (blob.size < MIN_RECORDING_BYTES) {
          callbacksRef.current?.onRecordingTooShort?.();
          return;
        }
        setIsFinalizing(true);
        const decoded = await extractPeaksFromBlob(blob);
        await onRecordingStop(blob, {
          waveform: decoded?.waveform ?? micWaveform,
          durationMs: decoded?.durationMs || micDurationMs,
        });
      } finally {
        stoppingRef.current = false;
        setIsFinalizing(false);
        mediaRecorderRef.current = null;
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };

    try {
      mediaRecorder.start(80);
    } catch {
      mediaRecorder.start();
    }
    setRecording(true);
  }, [onRecordingStop, recording, isFinalizing, startAnalyser, stopAnalyser]);

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || stoppingRef.current) return;
    if (recorder.state === "inactive") return;
    stoppingRef.current = true;
    try {
      recorder.requestData();
    } catch {
      // ignore
    }
    recorder.stop();
    setRecording(false);
  }, []);

  const cancelRecording = useCallback(() => {
    if (stoppingRef.current && mediaRecorderRef.current?.state === "inactive") return;
    cancelledRef.current = true;
    stoppingRef.current = true;
    mediaRecorderRef.current?.stop();
    setRecording(false);
    setRecordingTime(0);
    recordingTimeRef.current = 0;
  }, []);

  return {
    recording,
    recordingTime,
    startRecording,
    stopRecording,
    cancelRecording,
    isFinalizing,
    waveBars,
  };
}
